import React, { useState } from 'react';
import { Alert, Input, Modal, Typography, message } from 'antd';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { PVP_REASON_MAX, isPlayerId, pvpReviewBody } from '@/utils/pvpReview';
import type { PvpReviewPlayer } from '@/types/pvp';

const { Text } = Typography;

/** Who to flag: a listed player, or `byId` to type any player id in. */
export type FlagTarget = { player: PvpReviewPlayer } | { byId: true };

interface Props {
  target: FlagTarget | null;
  onClose: () => void;
  onFlagged: (player: PvpReviewPlayer) => void;
}

/**
 * Flags one player for PvP review, with an optional reason (stored as
 * "manual: <reason>"). The modal is the confirm: it says what a flag does
 * before the button is pressed.
 */
export const FlagPlayerModal: React.FC<Props> = ({ target, onClose, onFlagged }) => {
  const [idText, setIdText] = useState('');
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  const listed = target && 'player' in target ? target.player : null;
  const userId = listed ? listed.userId : idText.trim();
  const idValid = isPlayerId(userId);

  const close = () => {
    setIdText('');
    setReason('');
    onClose();
  };

  const flag = async () => {
    if (!idValid) return;
    setSending(true);
    try {
      const response = await adminApi.pvp.setReview(userId, pvpReviewBody(true, reason));
      message.success(`${response.data.displayName ?? 'Player'} flagged for PvP review`);
      onFlagged(response.data);
      close();
    } catch (error) {
      // 404: never played PvP; 400: not a player id.
      message.error(apiErrorMessage(error, 'Failed to flag the player'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={target !== null}
      title={
        listed
          ? `Flag ${listed.displayName ?? listed.userId} for PvP review?`
          : 'Flag a player by id'
      }
      okText="Flag player"
      okButtonProps={{ danger: true, disabled: !idValid, loading: sending }}
      onOk={flag}
      onCancel={close}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        From the next match they are paired in, their PvP matches are free: no entry fee and no
        coins won, and their opponent's match is free too. Coins already won stay, and their rating
        still counts. It is not a ban. If they are already flagged, this replaces the time and
        reason.
      </Text>
      {!listed && (
        <>
          <Input
            value={idText}
            onChange={(e) => setIdText(e.target.value)}
            placeholder="Player id (UUID)"
            aria-label="Player id"
            style={{ marginBottom: 4 }}
          />
          {idText.trim() !== '' && !idValid && (
            <Alert type="error" showIcon message="Not a player id" style={{ marginBottom: 8 }} />
          )}
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            Any player who has played PvP; the server says so if they never have.
          </Text>
        </>
      )}
      <Input.TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={PVP_REASON_MAX}
        showCount
        placeholder="Why (optional), e.g. reported by 3 players"
        aria-label="Reason"
      />
    </Modal>
  );
};
